import {
	allAdjustmentKeys,
	applyTemperatureBias,
	defaultReferenceId,
	FeatureDistances,
	getDistanceMap,
	getKNN,
	getNumRefsForColorMatch,
	getPresetAndBias,
	getUpdatedKNN,
	getValidNearestRefId,
	getValidNearestRefOverallDist,
	isEqual,
	refCurrentAdjustmentsId,
	saturationBiasBW,
} from "@/lib/ai/calibration/ai-calibration-feature"
import {
	getAdjustmentsFromVerySimilarNeighbor,
	getKeyAdjustments,
	getKeyAdjustmentsFast,
	getGlobalColorAdjustments,
} from "@/lib/ai/calibration/ai-calibration-keyadj"
import { ImageInfoRecord, PhotoRecord, StyleInfoRecord } from "@/lib/catalog"
import { getImageInfo, getImageInfoRecords } from "@/store/processing/image-info"
import { clamp, transformLocalAdjustments } from "@/utils/math"
import { getCullingInfo, photosAtom } from "@/store/photo/photos-state"
import { store } from "@/store/store"
import { fetchCurrentAdjustmentsFromHistory } from "@/store/store-history"
import {
	DefaultExcludedAdjustmentKeys,
	getAdjustmentRange,
	getDefaultAdjustments,
} from "@/core/adjustments"
import { AdjustmentKey, Adjustments, ColorProfileName, MaskGroup } from "@/core/types"
import { settingsAtom } from "@/store/settings-state"
import { findKeyNeighbor } from "./ai-cluster"
import {
	applyLocalAdjustmentsBias,
	getImageOrientationInfo,
} from "@/lib/ai/calibration/ai-calibration-mask"
import { logAiInfo } from "./utils"
import { transformPresetMaskForPaste } from "@/store/processing/processing-utils"
import { Sentry } from "@/telemetry/sentry"
import { isEmpty } from "./calibration/ai-calibration-utils"
import { isLocalStyle, referenceImagesAtomFamily, Style } from "@/store/style/style-state"
import { ClusterThreshold } from "@/lib/catalog/types"
import { hasReferenceWebpData256x256Source } from "@/lib/style/reference-webp-data"

export interface AIProcessingData {
	bias: Partial<Adjustments> | null
	presetAdjustments: Partial<Adjustments>
	keyAdjustments: Partial<Adjustments>
	keyPhotoColorAdjustments?: Partial<Adjustments>
	imageInfo: ImageInfoRecord
}

export function combineFinalAdjustments(
	adjustments: Adjustments,
	bias: Partial<Adjustments> | null,
	keyAdjustments: Partial<Adjustments>,
	imageInfo: ImageInfoRecord,
) {
	let finalAdjustments: Adjustments = {
		...adjustments,
	}

	const setValue = <K extends AdjustmentKey>(key: K, val: Adjustments[K]) => {
		finalAdjustments[key] = val
	}

	if (bias) {
		allAdjustmentKeys.forEach((key) => {
			if (DefaultExcludedAdjustmentKeys.includes(key)) return

			const adjustmentValue = finalAdjustments[key]
			const biasValue = bias[key]

			if (key === "colorProfile") {
				if (biasValue !== undefined) {
					setValue(key, biasValue as ColorProfileName | null)
				}
				return
			}

			if (adjustmentValue == null || biasValue == null) {
				return
			}

			if (typeof adjustmentValue === "number" && typeof biasValue === "number") {
				if (key === "temperature") {
					setValue(key, applyTemperatureBias(adjustmentValue, biasValue))
				} else {
					setValue(key, clamp(adjustmentValue + biasValue, ...getAdjustmentRange(key)))
				}
			} else if (biasValue && (key.includes("curves") || key === "pointColors")) {
				setValue(key, biasValue)
			} else if (biasValue && adjustmentValue && key === "localAdjustments") {
				setValue(
					key,
					applyLocalAdjustmentsBias(
						adjustmentValue as MaskGroup[],
						biasValue as MaskGroup[],
					),
				)
			}
		})
	}

	// This localAdj is from preset, but need to redo the transform to include cropping etc. from auto or key adjustments.
	if (finalAdjustments.localAdjustments) {
		const oriInfo = getImageOrientationInfo(imageInfo.features)

		// To on-screen coord
		const presetLocalAdjustments = transformLocalAdjustments(
			finalAdjustments.localAdjustments,
			getDefaultAdjustments(),
			oriInfo.originalImageSize,
			oriInfo.initialOrientation,
			true,
		)

		// To texture coord with crop/straighten in auto/key adjustments
		finalAdjustments.localAdjustments = transformLocalAdjustments(
			presetLocalAdjustments,
			{ ...finalAdjustments, ...keyAdjustments },
			oriInfo.originalImageSize,
			oriInfo.initialOrientation,
			false,
		)
	}

	// add manual local adjustments from keyAdjustments instead of overwriting
	const keyAdjustmentsCopy = { ...keyAdjustments }
	if (finalAdjustments.localAdjustments && keyAdjustments.localAdjustments) {
		finalAdjustments.localAdjustments = finalAdjustments.localAdjustments.concat(
			keyAdjustments.localAdjustments,
		)
		delete keyAdjustmentsCopy.localAdjustments
	}

	finalAdjustments = {
		...finalAdjustments,
		...keyAdjustmentsCopy,
	}

	return finalAdjustments
}

async function getCurrentAdjustments(photo: PhotoRecord, imageInfo: ImageInfoRecord) {
	let currentAdjustments =
		photo.kind === "reference" ? imageInfo.aiAdjustmentsMap[refCurrentAdjustmentsId] : undefined
	if (!currentAdjustments) {
		currentAdjustments = await fetchCurrentAdjustmentsFromHistory(photo)
	}

	return currentAdjustments as Adjustments
}

// preset and bias are already computed and stored in imageInfo during updateAIStyle,
// only compute keyAdjustments from keyImageInfo here
async function getReprocessingData(
	imageInfo: ImageInfoRecord,
	style: Style,
): Promise<AIProcessingData> {
	logAiInfo("[getReprocessingData] apply updated ai style for", imageInfo.imageId)

	const keyNeighborId = imageInfo.keyNeighborId ?? imageInfo.imageId
	const keyPhotoRecord = store.get(photosAtom)[keyNeighborId]
	const keyImageInfo = getImageInfo(keyNeighborId)
	const bias = imageInfo.biases
	let presetAdjustments: Partial<Adjustments> = {}
	if (imageInfo.presetId) {
		presetAdjustments = style.presets[imageInfo.presetId] ?? {}
	}

	// transfer wb, adjusted light params etc. from the key image in a cluster
	let keyAdjustments: Partial<Adjustments> = {}
	let keyPhotoColorAdjustments: Partial<Adjustments> = {}
	if (keyImageInfo && keyPhotoRecord && imageInfo.imageId != keyImageInfo.imageId) {
		const res = await getAdjustmentsFromKeyOrNeighbor({
			imageInfo,
			keyPhotoRecord,
			keyImageInfo,
		})
		keyAdjustments = res.keyAdjustments
		keyPhotoColorAdjustments = res.keyPhotoColorAdjustments
	}

	return {
		presetAdjustments,
		bias,
		keyAdjustments,
		imageInfo,
		keyPhotoColorAdjustments,
	}
}

async function getAdjustmentsFromKeyOrNeighbor({
	imageInfo,
	keyPhotoRecord,
	keyImageInfo,
}: {
	imageInfo: ImageInfoRecord
	keyPhotoRecord: PhotoRecord
	keyImageInfo: ImageInfoRecord
}) {
	let keyAdjustments: Partial<Adjustments> = {}
	let keyPhotoColorAdjustments: Partial<Adjustments> = {}
	const currentAdjustments = await getCurrentAdjustments(keyPhotoRecord, keyImageInfo)
	keyAdjustments = await getKeyAdjustments({
		imageInfo,
		keyImageInfo,
		keyImageCurrentAdjustments: currentAdjustments,
		settings: store.get(settingsAtom),
	})

	// If not use key's light params, check if very similar neighbor has been processed and has full adjustments
	if (keyAdjustments.exposure == undefined) {
		const fullAdjustments = getAdjustmentsFromVerySimilarNeighbor(imageInfo)
		if (fullAdjustments) {
			logAiInfo(
				"use full adjustments from very similar neighbor",
				imageInfo.features.verySimilarNeighbor,
			)
			keyAdjustments.exposure = fullAdjustments.exposure
			delete keyAdjustments.saturation
			keyPhotoColorAdjustments = fullAdjustments
		}
	}

	if (isEmpty(keyPhotoColorAdjustments)) {
		keyPhotoColorAdjustments = getGlobalColorAdjustments(currentAdjustments)
	}

	return { keyAdjustments, keyPhotoColorAdjustments }
}

/**
 * Consistency Mechanism 3: adopt the reference set of a nearby, already-processed key photo
 * if it is visually closer than the current photo's own nearest reference.
 *
 * This prevents a visible color shift at cluster boundaries where two adjacent clusters
 * independently select different references despite being shot in the same scene.
 *
 * Strategy:
 * 1. Find all processed key photos whose timestamps are within 5 minutes of this photo.
 * 2. Among the 5 closest in time, pick the one with the smallest overall feature distance.
 * 3. If that neighbour is closer than the current nearest-reference distance × ratio,
 *    inherit its reference set (re-ranked by distance to *this* photo).
 *
 * The adoption ratio scales with temporal proximity [0.7, 0.9]: photos shot moments apart
 * need to be less visually similar to trigger adoption than photos shot further apart.
 */
function tryAdoptRefsFromSimilarKeyPhoto(
	imageInfo: ImageInfoRecord,
	kNeighbors: [string, FeatureDistances][],
	referenceImages: Record<string, ImageInfoRecord>,
): [string, FeatureDistances][] {
	const thisTime = imageInfo.features.meta.timestamp ?? 0
	const timeThreshMs = 5 * 60 * 1000

	// Collect already-processed key photos within the time window
	const nearbyKeyPhotos: ImageInfoRecord[] = []
	getImageInfoRecords().forEach((info) => {
		const thatTime = info.features.meta.timestamp ?? 0
		if (
			info.biases &&
			info.keyNeighborId == info.imageId &&
			thatTime &&
			Math.abs(thatTime - thisTime) <= timeThreshMs
		) {
			nearbyKeyPhotos.push(info)
		}
	})

	// Sort by temporal proximity, then take the 5 closest and find the visually nearest
	nearbyKeyPhotos.sort(
		(a, b) =>
			Math.abs(a.features.meta.timestamp ?? 0 - thisTime) -
			Math.abs(b.features.meta.timestamp ?? 0 - thisTime),
	)
	const candidates = nearbyKeyPhotos.slice(0, 5)
	if (candidates.length === 0) return kNeighbors

	const candidateDistanceMap = getDistanceMap(
		imageInfo,
		Object.fromEntries(candidates.map((info) => [info.imageId, info])),
	)
	const bestCandidate = candidates.reduce((prev, current) =>
		(candidateDistanceMap[current.imageId]?.[4] ?? 0) <
		(candidateDistanceMap[prev.imageId]?.[4] ?? 0)
			? current
			: prev,
	)

	const nearestRefDist = getValidNearestRefOverallDist(kNeighbors)
	const candidateDist = candidateDistanceMap[bestCandidate.imageId]?.[4]
	const timeDiffSec = Math.abs((bestCandidate.features.meta.timestamp ?? 0) - thisTime) / 1000
	// Ratio range [0.7, 0.9]: adoption threshold tightens as the time gap grows
	const ratio = 0.7 + (Math.max(0, 60 - timeDiffSec) / 60) * 0.2

	logAiInfo("tryAdoptRefsFromSimilarKeyPhoto", {
		bestCandidate,
		candidateDist,
		nearestRefDist,
		timeDiffSec,
		ratio,
	})

	if (!candidateDist || candidateDist >= nearestRefDist * ratio) return kNeighbors

	// Re-rank the candidate's references by distance to *this* photo
	const adoptedRefImages =
		bestCandidate.nearestReferences?.reduce(
			(acc, [refId]) => {
				const ref = referenceImages[refId]
				if (ref) acc[refId] = ref
				return acc
			},
			{} as typeof referenceImages,
		) ?? {}

	const newKNeighbors = getKNN(getDistanceMap(imageInfo, adoptedRefImages), false)
	if (newKNeighbors.length > 0) {
		logAiInfo("Adopted refs from nearby key photo", imageInfo.imageId, adoptedRefImages)
		return newKNeighbors
	}
	return kNeighbors
}

function computeProcessingDataFromReferences(
	imageInfo: ImageInfoRecord,
	style: Style,
	checkForSimilarKeyPhotos: boolean = true,
): AIProcessingData {
	logAiInfo("computeProcessingDataFromReferences for", imageInfo.imageId)
	const referenceImages = isLocalStyle(style)
		? store.get(referenceImagesAtomFamily())
		: style.referenceImages

	const distance = getDistanceMap(imageInfo, referenceImages)
	logAiInfo("distanceMap for ", imageInfo.imageId, distance)
	let kNeighbors = getKNN(distance)

	if (checkForSimilarKeyPhotos) {
		kNeighbors = tryAdoptRefsFromSimilarKeyPhoto(imageInfo, kNeighbors, referenceImages)
	}

	const { presetId, bias } = getPresetAndBias(imageInfo, kNeighbors, referenceImages)

	let presetAdjustments: Partial<Adjustments> = {}
	if (presetId) {
		presetAdjustments = style.presets[presetId] ?? {}
	}

	// finally apply the preset and do auto adjustments as before, and add bias
	imageInfo.presetId = presetId
	imageInfo.biases = bias
	imageInfo.nearestReferences = kNeighbors

	return {
		presetAdjustments,
		bias,
		keyAdjustments: {},
		imageInfo: imageInfo,
	}
}

async function getProcessingDataFromKeyNeighbor(imageInfo: ImageInfoRecord, style: Style) {
	const keyId = imageInfo.keyNeighborId ?? imageInfo.imageId
	const keyImageInfo = getImageInfo(keyId)

	if (!keyImageInfo) {
		throw new Error("Could not find key neighbor image info")
	}

	logAiInfo("getProcessingDataFromKeyNeighbor for", imageInfo.imageId, "keyId:", keyId)

	const presetId = keyImageInfo.presetId
	let presetAdjustments: Partial<Adjustments> = {}
	if (presetId) {
		presetAdjustments = style.presets[presetId] ?? {}
	}
	const keyPhotoRecord = store.get(photosAtom)[keyId]

	let keyAdjustments: Partial<Adjustments> = {}
	let keyPhotoColorAdjustments: Partial<Adjustments> = {}
	if (!keyPhotoRecord) {
		console.log(`Key photo record missing. ImageId: ${imageInfo.imageId} and keyId: ${keyId}`)
		// Static message so Sentry groups all occurrences into one issue
		Sentry.captureMessage("Key photo record missing", {
			level: "log",
			extra: { imageId: imageInfo.imageId, keyId },
		})

		// TODO @din: potentially try to find new key photo based on changed identifiers?
	} else {
		const res = await getAdjustmentsFromKeyOrNeighbor({
			imageInfo,
			keyPhotoRecord,
			keyImageInfo,
		})
		keyAdjustments = res.keyAdjustments
		keyPhotoColorAdjustments = res.keyPhotoColorAdjustments
	}

	imageInfo.presetId = keyImageInfo.presetId
	imageInfo.biases = keyImageInfo.biases
	imageInfo.nearestReferences = keyImageInfo.nearestReferences

	return {
		presetAdjustments,
		keyAdjustments,
		bias: keyImageInfo.biases,
		imageInfo,
		keyPhotoColorAdjustments,
	}
}

export async function calculateAIProcessingData(
	style: Style,
	imageInfo: ImageInfoRecord,
	ignoreClustering: boolean = false,
): Promise<AIProcessingData> {
	if (ignoreClustering) {
		// Make a copy to avoid modifying the original imageInfo for style-preview photos, as user may cancel and return to old style
		// The returned imageInfo is still modified but not stored during preview processing
		const tempImageInfo = { ...imageInfo }
		tempImageInfo.keyNeighborId = tempImageInfo.imageId
		logAiInfo("Ignore clustering, set self as keyNeighbor")
		return computeProcessingDataFromReferences(tempImageInfo, style, false)
	}

	// Reprocessing: get updated style preset, bias from imageInfo
	if (imageInfo.aiStyleAdjustments) {
		const result = await getReprocessingData(imageInfo, style)
		if (result.bias) {
			return result
		} else {
			// something is wrong, needs to start over and run initial processing below
			imageInfo = result.imageInfo
			logAiInfo("Somehow need to start over and run initial processing")
		}
	}

	// Initial processing

	// step 1: do clustering - find keyNeighbor
	// Set keyNeighborId and verySimilarNeighbor from culling info computed from pre-clustering
	const cullingInfo = getCullingInfo(imageInfo.imageId)
	let keyID = cullingInfo?.clusterLeaderIdMap?.[ClusterThreshold.EditClustering] ?? null
	let verySimilarNeighbor =
		cullingInfo?.clusterLeaderIdMap?.[ClusterThreshold.VerySimilar] ?? null
	// If the resolved key photo is unavailable (e.g. hidden, locked, or skipped), find a key on the fly.
	if (keyID && keyID !== imageInfo.imageId) {
		const keyImageInfo = getImageInfo(keyID)
		if (keyImageInfo && keyImageInfo.keyNeighborId) {
			// This is the true key photo ID, in case the key photo has been changed since pre-clustering
			keyID = keyImageInfo.keyNeighborId
		}
		const resolvedKeyInfo = getImageInfo(keyID)
		const resolvedKeyPhoto = store.get(photosAtom)[keyID]
		if (!resolvedKeyInfo || !resolvedKeyInfo.biases || resolvedKeyPhoto?.kind === "locked") {
			logAiInfo(
				`[Cluster] Key photo ${keyID} from cullingInfo of ${imageInfo.imageId} is unavailable, finding key on the fly`,
			)
			keyID = null
		}
	}

	if (!keyID) {
		// If no valid keyID from pre-clustering, find it on the fly
		const result = findKeyNeighbor(imageInfo)
		keyID = result.keyID
		verySimilarNeighbor = result.verySimilarNeighbor
		logAiInfo(
			`[Cluster] On the fly found keyNeighbor=${keyID} and verySimilarNeighbor=${verySimilarNeighbor}`,
		)
	} else {
		logAiInfo(
			`[Cluster] KeyNeighbor already set by preClustering, keyId=${keyID} and verySimilarNeighbor=${verySimilarNeighbor}`,
		)
	}
	if (keyID) {
		imageInfo.keyNeighborId = keyID
		// If verySimilarNeighbor is found, need to use its useOwnAutoLight flag to ensure consistent look for very similar photos
		if (verySimilarNeighbor) {
			imageInfo.features.useOwnAutoLightMap = {
				...getImageInfo(verySimilarNeighbor)?.features.useOwnAutoLightMap,
			}
			imageInfo.features.verySimilarNeighbor = verySimilarNeighbor
			logAiInfo(
				`Found verySimilarNeighbor: ${verySimilarNeighbor}`,
				"useOwnAutoLightMap:",
				imageInfo.features.useOwnAutoLightMap,
			)
		} else {
			imageInfo.features.useOwnAutoLightMap = {} // to be computed later
		}
	} else {
		imageInfo.keyNeighborId = imageInfo.imageId
		logAiInfo("Use self as keyNeighbor")
	}

	if (imageInfo.keyNeighborId === imageInfo.imageId) {
		// step 2: find nearest neighbors and determine the preset and bias
		return computeProcessingDataFromReferences(imageInfo, style)
	} else {
		// step 2: or directly use the already-processed keyImage's nearest neighbors, preset, bias, and keyAdjustments

		// Safe guard: if the key image is not processed due to hidden or wrong priority order, fall back to compute the processing data from references
		const keyImageInfo = getImageInfo(imageInfo.keyNeighborId)
		if (!keyImageInfo || !keyImageInfo.biases) {
			const message = `Key photo was not processed first.`
			console.warn(message, { imageInfo, keyImageInfo })
			Sentry.captureMessage(message, {
				level: "warning",
				extra: {
					imageInfo,
					keyImageInfo,
					clusterLeaderIdMap: cullingInfo?.clusterLeaderIdMap,
				},
			})
			imageInfo.keyNeighborId = imageInfo.imageId
			return computeProcessingDataFromReferences(imageInfo, style)
		}

		return getProcessingDataFromKeyNeighbor(imageInfo, style)
	}
}

/**
 *
 * @param imageInfo
 * @param style
 * @param newRefPhotos any newly added, or modified existing reference photos should be passed in here.
 * @param removedRefPhotos any newly removed (unapproved) reference photos should be passed in here.
 * @returns
 */
export function getNewKNNForReprocessing(
	imageInfo: ImageInfoRecord,
	referenceImages: Record<string, ImageInfoRecord>,
	newRefPhotos: Record<string, ImageInfoRecord>,
	removedRefPhotos?: Record<string, ImageInfoRecord>,
) {
	logAiInfo("[getNewKNNForReprocessing] check keyPhoto reprocess style for", imageInfo.imageId, {
		imageInfo,
	})

	// Step 1: find cases in which we need to recompute all distance to find new KNN
	let computeKNNFromScratch = false

	// Safe guard for missing nearestReferences
	// newly approved/disapproved photo and its cluster members could have nearestReferences == null
	if (!imageInfo.nearestReferences) {
		logAiInfo(`[getNewKNNForReprocessing] ${imageInfo.imageId} missing nearestReferences`)
		computeKNNFromScratch = true
	}

	if (!computeKNNFromScratch && removedRefPhotos && Object.keys(removedRefPhotos).length !== 0) {
		const oldReferences = imageInfo.nearestReferences?.map((item) => item[0])
		for (const removedImageID of Object.keys(removedRefPhotos)) {
			// if one of the newly removed ref belongs to the old references
			if (
				oldReferences?.includes(removedImageID) ||
				// or if this image is just removed from references
				removedImageID === imageInfo.imageId
			) {
				computeKNNFromScratch = true
				break
			}
		}
	}

	// Safe guard for previously removed references still apprear in nearestReferences somehow
	if (imageInfo.nearestReferences && !computeKNNFromScratch) {
		let filteredReferences = imageInfo.nearestReferences.filter(
			(item) => item[0] in referenceImages || item[0] === defaultReferenceId,
		)
		// exclude existing B&W ref (for old project compatibility)
		filteredReferences = filteredReferences.filter(
			(item) => referenceImages[item[0]]?.biases?.saturation !== saturationBiasBW,
		)
		if (filteredReferences.length != imageInfo.nearestReferences.length) {
			console.warn(
				`[getNewKNNForReprocessing] some of the imageInfo.nearestReferences not exist in style.referenceImages`,
				imageInfo.nearestReferences,
			)
			computeKNNFromScratch = true
		}
	}

	if (computeKNNFromScratch) {
		const distance = getDistanceMap(imageInfo, referenceImages)
		const kNeighborsNew = getKNN(distance)
		return { kNeighborsNew, updated: true }
	}

	// Step 2: only compute distance with the new ref photos and determine if KNN is updated

	// Filter new ref photos according to timestamp to avoid affect too-far photos
	const thisTime = imageInfo.features.meta.timestamp ?? 0
	const timeDiffThresh = 60 // in minutes
	const filteredNewRefs: Record<string, ImageInfoRecord> = Object.fromEntries(
		Object.entries(newRefPhotos).filter(
			([_, value]) =>
				Math.abs((value.features.meta.timestamp ?? thisTime) - thisTime) / 60000 <=
				timeDiffThresh,
		),
	)

	const distance = getDistanceMap(imageInfo, filteredNewRefs)
	logAiInfo("[getNewKNNForReprocessing] new distance:", {
		distance,
		existingKNN: imageInfo.nearestReferences,
	})

	const { kNeighborsNew, updated } = getUpdatedKNN(
		distance,
		imageInfo.nearestReferences as [string, FeatureDistances][],
	)
	return { kNeighborsNew, updated }
}

// Update imageInfo of a photo when new reference photos are added or updated or removed
export function prepareForApplyUpdatedAIStyle(options: {
	imageInfo: ImageInfoRecord
	referenceImages: Record<string, ImageInfoRecord>
	newRefPhotos: Record<string, ImageInfoRecord>
	removedRefPhotos?: Record<string, ImageInfoRecord>
}) {
	const { imageInfo, referenceImages, newRefPhotos, removedRefPhotos } = options
	const { kNeighborsNew, updated } = getNewKNNForReprocessing(
		imageInfo,
		referenceImages,
		newRefPhotos,
		removedRefPhotos,
	)

	if (updated) {
		const { presetId, bias } = getPresetAndBias(imageInfo, kNeighborsNew, referenceImages)
		const oldRefId = getValidNearestRefId(imageInfo.nearestReferences)
		const newRefId = getValidNearestRefId(kNeighborsNew)
		const noChangeInNearestRef =
			oldRefId && newRefId && oldRefId === newRefId && !newRefPhotos[newRefId]
		const nearestRef = newRefId ? referenceImages[newRefId] : null

		// new algorithm (preset + half color match)
		// when NumRefsForColorMatch == 1, only depends on nearest reference, not the second/third nearest
		// when NumRefsForColorMatch > 1, it depends all K nearest references; however, we still only reprocess when nearest ref or preset or num of KNN changes to avoid too many reprocessing
		if (
			nearestRef &&
			nearestRef.colorMatchReference &&
			hasReferenceWebpData256x256Source(nearestRef)
		) {
			if (
				noChangeInNearestRef &&
				presetId === imageInfo.presetId &&
				kNeighborsNew.length === imageInfo.nearestReferences?.length
			) {
				logAiInfo(
					"[prepareForApplyUpdatedAIStyle] keyPhoto: No change in nearest reference or preset, skip color match for",
					imageInfo.imageId,
				)
			} else {
				logAiInfo(
					`[prepareForApplyUpdatedAIStyle] keyPhoto: Use color match for ${imageInfo.imageId}, ref changed from ${oldRefId} to ${newRefId}`,
				)
				imageInfo.needApplyStyle = true
			}

			imageInfo.presetId = presetId
			imageInfo.biases = {}
			imageInfo.nearestReferences = kNeighborsNew

			return imageInfo
		}

		// old algorithm (preset + auto + bias) depends on both nearest and second nearest reference
		imageInfo.nearestReferences = kNeighborsNew

		// Check if preset and bias changes, if no change we don't need to reprocess it.
		const prevBiases = imageInfo.biases
		if (
			presetId == imageInfo.presetId &&
			prevBiases &&
			isEqual(bias, prevBiases, DefaultExcludedAdjustmentKeys)
		) {
			logAiInfo(
				"[prepareForApplyUpdatedAIStyle] keyPhoto: No AI updates even with new KNN for old algorithm as bias/preset is unchanged.",
			)
			return imageInfo // updates unavailable
		}

		imageInfo.needApplyStyle = true
		imageInfo.presetId = presetId
		imageInfo.biases = bias
	}

	return imageInfo
}

export function isFinalAdjustmentsUpdated(input: {
	photo: PhotoRecord
	imageInfo: ImageInfoRecord
	updatedInfo: ImageInfoRecord
	keyInfo: ImageInfoRecord
	style: StyleInfoRecord
}): boolean {
	// have to compare finalAdjustments with currentAdjustments to know if real updates are available:
	// such as keyPhoto only has straighten change that's not transferrable to the member
	// need to compare finalAdjustment instead of keyAdjustments because bias could change for light/other

	const { photo, imageInfo, updatedInfo, keyInfo, style } = input

	if (!imageInfo.biases || !updatedInfo.biases || !keyInfo.biases) {
		return true
	}

	// Assume color match always needs reprocess
	if (!imageInfo.presetId || !keyInfo.presetId) {
		return true
	}
	if (keyInfo.colorMatchReference && hasReferenceWebpData256x256Source(keyInfo)) {
		return true
	}

	const aiAdjustments = imageInfo.presetId ? imageInfo.aiAdjustmentsMap[imageInfo.presetId] : null
	const existingKeyAdjustments = imageInfo.aiStyleAdjustments

	const keyImageCurrAdjustments = keyInfo.aiAdjustmentsMap[refCurrentAdjustmentsId]

	if (!aiAdjustments || !existingKeyAdjustments || !keyImageCurrAdjustments) {
		return true
	}
	const defaultAdjustments = getDefaultAdjustments(photo)

	const presetAdjustments = imageInfo.presetId
		? style.presets[imageInfo.presetId]
		: defaultAdjustments
	if (!presetAdjustments || imageInfo.presetId != keyInfo.presetId) {
		return true
	}
	const transformedPresetAdjustments = transformPresetMaskForPaste(
		imageInfo,
		presetAdjustments,
		defaultAdjustments,
	)
	const aiAdjustmentsFull = {
		...defaultAdjustments,
		...transformedPresetAdjustments,
		...aiAdjustments,
	}

	const keyAdjustments = getKeyAdjustmentsFast({
		imageInfo: updatedInfo,
		keyImageInfo: keyInfo,
		keyImageCurrentAdjustments: { ...defaultAdjustments, ...keyImageCurrAdjustments },
		settings: store.get(settingsAtom),
	})

	// derive new aiStyleAdjustments
	const finalAdjustments = combineFinalAdjustments(
		aiAdjustmentsFull,
		updatedInfo.biases,
		keyAdjustments,
		updatedInfo,
	)

	// derive existing aiStyleAdjustments
	// existingKeyAdjustments only stored key adjustments in older versions, but stored full adjustments in newer versions
	let currentAdjustments = existingKeyAdjustments
	if (Object.keys(currentAdjustments).length < 12) {
		currentAdjustments = combineFinalAdjustments(
			aiAdjustmentsFull,
			imageInfo.biases,
			existingKeyAdjustments,
			imageInfo,
		)
	}

	// compare
	const temperatureThresh = (currentAdjustments["temperature"] ?? 5000) * 0.015
	if (isEqual(currentAdjustments, finalAdjustments, null, temperatureThresh)) {
		return false
	} else {
		return true
	}
}

export function adaptExposureOnlyFromKeyPhoto(
	imageInfo: ImageInfoRecord,
	keyAdjustments: Partial<Adjustments>,
) {
	return (
		imageInfo.keyNeighborId &&
		imageInfo.keyNeighborId !== imageInfo.imageId &&
		keyAdjustments.exposure != undefined
	)
}

export function applyReverseStyleWBBiases(
	adjustments: Partial<Adjustments>,
	imageInfo: ImageInfoRecord,
	referenceImages: Record<string, ImageInfoRecord>,
) {
	const biases = getWeightedStyleBiases(imageInfo, referenceImages)

	if (adjustments.temperature != undefined && biases.temperature) {
		const asShotTemperature = imageInfo.features.meta.init_temp
		adjustments.temperature = clamp(
			adjustments.temperature - biases.temperature * asShotTemperature,
			...getAdjustmentRange("temperature"),
		)
	}

	if (adjustments.tint != undefined && biases.tint) {
		adjustments.tint = clamp(adjustments.tint - biases.tint, ...getAdjustmentRange("tint"))
	}

	return adjustments
}

function getWeightedStyleBiases(
	imageInfo: ImageInfoRecord,
	referenceImages: Record<string, ImageInfoRecord>,
): Partial<Adjustments> {
	const numRefs = getNumRefsForColorMatch()
	const nearestReferences = imageInfo.nearestReferences ?? []

	// Collect all possible keys from styleBiases
	const allKeys = new Set<string>()
	for (const ref of nearestReferences) {
		const refId = ref[0]
		const refInfo = referenceImages[refId]
		const refBiases = refInfo?.styleBiases
		if (refBiases) {
			Object.keys(refBiases).forEach((key) => allKeys.add(key))
		}
	}

	// Initialize biases object dynamically with all keys
	const biases: Partial<Adjustments> = {}
	allKeys.forEach((key) => {
		biases[key] = 0
	})

	const weights = [0.4, 0.3, 0.3]
	let totalWeight = 0
	let i = 0

	for (const ref of nearestReferences) {
		const refId = ref[0]
		const refInfo = referenceImages[refId]
		const refBiases = refInfo?.styleBiases
		if (refBiases) {
			const weight = weights[i] ?? 0

			// Apply weight to all bias keys dynamically
			Object.keys(refBiases).forEach((key) => {
				if (typeof refBiases[key] === "number") {
					biases[key] = (biases[key] || 0) + (refBiases[key] ?? 0) * weight
				}
			})

			totalWeight += weight
			i++
		}
		if (i >= numRefs) {
			break
		}
	}

	// Normalize by total weight for all keys
	if (totalWeight > 0 && totalWeight !== 1) {
		Object.keys(biases).forEach((key) => {
			if (biases[key] !== undefined) {
				biases[key] /= totalWeight
			}
		})
	}

	return biases
}

export function applyWeightedStyleBiases(
	adjustments: Partial<Adjustments>,
	imageInfo: ImageInfoRecord,
	referenceImages: Record<string, ImageInfoRecord>,
	keyAdjustments: Partial<Adjustments>,
) {
	if (adaptExposureOnlyFromKeyPhoto(imageInfo, keyAdjustments)) {
		return adjustments
	}

	// Key photo is reference, no need to apply style biases
	if (!imageInfo.nearestReferences?.length) {
		return adjustments
	}

	const biases = getWeightedStyleBiases(imageInfo, referenceImages)

	logAiInfo("[applyWeightedStyleBiases] weighted biases:", biases)
	logAiInfo("[applyWeightedStyleBiases] before:", {
		exposure: adjustments.exposure,
		contrast: adjustments.contrast,
		saturation: adjustments.saturation,
		temperature: adjustments.temperature,
	})

	adjustments = applyStyleBiases({
		adjustments,
		styleBiases: biases,
		asShotTemperature: imageInfo.features.meta.init_temp,
	})

	logAiInfo("[applyWeightedStyleBiases] after:", {
		exposure: adjustments.exposure,
		contrast: adjustments.contrast,
		saturation: adjustments.saturation,
		temperature: adjustments.temperature,
	})

	return adjustments
}

export function applyStyleBiases(options: {
	adjustments: Partial<Adjustments>
	styleBiases: Partial<Adjustments>
	asShotTemperature: number | undefined
}) {
	const { adjustments, styleBiases, asShotTemperature } = options

	for (const key in styleBiases) {
		const delta = styleBiases[key]
		const current = adjustments[key]

		if (current === undefined || !delta) continue
		if (key === "temperature" && typeof current === "number") {
			if (asShotTemperature) {
				adjustments.temperature = clamp(
					current + delta * asShotTemperature,
					...getAdjustmentRange("temperature"),
				)
			}
		} else {
			adjustments[key] = clamp(
				(current as number) + delta,
				...getAdjustmentRange(key as keyof Adjustments),
			)
		}
	}
	return adjustments
}
